import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Link,
  Divider,
  Card,
  CardContent,
  Button,
  Alert,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import EmailIcon from '@mui/icons-material/Email';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LanguageIcon from '@mui/icons-material/Language';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import GavelIcon from '@mui/icons-material/Gavel';
import { useNavigate } from 'react-router-dom';

const Support: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Support - Daily Stories</title>
        <meta name="description" content="Get help with Daily Stories. Contact support, view FAQs, and find answers to common questions about our app and subscription." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 300 }}>
              Daily Stories Support
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
              We're here to help you with any questions or concerns
            </Typography>
          </Box>

          {/* Age Verification & Content Warning */}
          <Alert 
            severity="warning" 
            icon={<WarningAmberIcon />}
            sx={{ mb: 4, borderRadius: 2 }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              18+ Adult Content Only
            </Typography>
            <Typography variant="body2" paragraph>
              Daily Stories contains explicit adult content intended exclusively for users 18 years of age or older. By using this app, you confirm that you are of legal age in your jurisdiction to view adult content.
            </Typography>
            <Typography variant="body2">
              <strong>Parents & Guardians:</strong> This app is not suitable for minors. Please use parental controls on your device to restrict access to age-inappropriate content.
            </Typography>
          </Alert>

          {/* Important Disclaimers */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'warning.light', borderRadius: 2, border: 1, borderColor: 'warning.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <InfoIcon sx={{ fontSize: 32, mr: 2, color: 'warning.dark' }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Important Disclaimers
              </Typography>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Content Advisory
              </Typography>
              <Typography variant="body2" paragraph>
                All stories published on Daily Stories are works of fiction. Any resemblance to actual persons, living or dead, or actual events is purely coincidental. The content is created for entertainment purposes only and should not be considered as advice, instruction, or endorsement of any activities depicted.
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Subscription & Billing
              </Typography>
              <Typography variant="body2" paragraph>
                • Subscription price: $4.99/month (USD)
                <br />
                • Payment will be charged to your Apple ID account at confirmation of purchase
                <br />
                • Subscription automatically renews unless canceled at least 24 hours before the end of the current period
                <br />
                • Your account will be charged for renewal within 24 hours prior to the end of the current period
                <br />
                • Subscriptions may be managed and auto-renewal can be turned off in your Account Settings after purchase
                <br />
                • No refunds for unused portions of the subscription period
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Consent & Safety
              </Typography>
              <Typography variant="body2" paragraph>
                Daily Stories promotes consensual adult relationships and safe practices. All content depicts fictional scenarios between consenting adults. We do not condone or promote any illegal activities, non-consensual acts, or harmful behavior.
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Responsible Use
              </Typography>
              <Typography variant="body2">
                This app is designed for private, personal use. Users are responsible for ensuring their use of the app complies with local laws and regulations. We recommend using the app in a private setting and being mindful of others who may inadvertently view your screen.
              </Typography>
            </Box>
          </Paper>

          {/* Contact Card */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmailIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                Contact Us
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              For support inquiries, questions, or feedback, please contact us at:
            </Typography>
            <Link
              href="mailto:cem@plotcamp.com"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              cem@plotcamp.com
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              We typically respond within 24-48 hours
            </Typography>
          </Paper>

          {/* App Information */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={0} sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PhoneIphoneIcon sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h6">Mobile App</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>App Name:</strong> Daily Stories 18+
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Version:</strong> 1.0.0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Bundle ID:</strong> com.plotcamp.dailystories
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Subscription:</strong> $4.99/month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={0} sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LanguageIcon sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h6">Website</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>URL:</strong>{' '}
                    <Link href="https://dailystories.fr" target="_blank" rel="noopener">
                      dailystories.fr
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Content:</strong> Daily sophisticated erotic stories
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Settings:</strong> European cities, LGBTQ+ inclusive
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* FAQ Section */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <HelpOutlineIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                Frequently Asked Questions
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                How do I subscribe to Daily Stories?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                You can subscribe through the mobile app for $4.99/month. The subscription is managed through your Apple App Store or Google Play account.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                How do I cancel my subscription?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                To cancel your subscription:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>iOS:</strong> Settings → Your Name → Subscriptions → Daily Stories → Cancel Subscription
                <br />
                <strong>Android:</strong> Google Play Store → Menu → Subscriptions → Daily Stories → Cancel Subscription
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                How often are new stories published?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We publish a new story every day at midnight. Each story is set in a different European city and features diverse characters and orientations.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                What do the intensity ratings mean?
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>0 flames:</strong> Non-erotic (romantic, emotional connection only)
                <br />
                <strong>1 flame:</strong> Mild (sensual, implied intimacy)
                <br />
                <strong>2 flames:</strong> Moderate (explicit but tasteful)
                <br />
                <strong>3 flames:</strong> Intense (very explicit, fetish content)
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Is my data secure?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Yes. We take your privacy seriously. We do not collect or store personal information like your name or email (unless you contact us). Payment information is handled entirely by Apple or Google. See our{' '}
                <Link onClick={() => navigate('/privacy-policy')} sx={{ cursor: 'pointer' }}>
                  Privacy Policy
                </Link>{' '}
                for details.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                I'm having technical issues with the app
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please email us at{' '}
                <Link href="mailto:cem@plotcamp.com">cem@plotcamp.com</Link> with:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 3 }}>
                <li>Your device type (iPhone, Android)</li>
                <li>App version</li>
                <li>Description of the issue</li>
                <li>Screenshots if applicable</li>
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Can I get a refund?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Refund requests must be made directly through Apple or Google, as they process all payments. Daily Stories does not have access to process refunds directly.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>iOS:</strong> Visit <Link href="https://support.apple.com/en-us/HT204084" target="_blank" rel="noopener">Apple Support</Link> to request a refund
                <br />
                <strong>Android:</strong> Visit <Link href="https://support.google.com/googleplay/answer/2479637" target="_blank" rel="noopener">Google Play Support</Link> to request a refund
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Is the content safe and consensual?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Yes. All stories feature consenting adults in fictional scenarios. We have strict content guidelines that prohibit:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 3 }}>
                <li>Non-consensual activities</li>
                <li>Minors or content suggesting underage participants</li>
                <li>Illegal activities</li>
                <li>Violence or abuse</li>
                <li>Harmful or dangerous practices</li>
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                How do I delete my account and data?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Since we don't require account creation, there's no account to delete. All your preferences and favorites are stored locally on your device. To remove all data:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 3 }}>
                <li>Cancel your subscription (if active)</li>
                <li>Uninstall the Daily Stories app from your device</li>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This will permanently remove all locally stored data. If you have any concerns about data privacy, please contact us at cem@plotcamp.com.
              </Typography>
            </Box>
          </Paper>

          {/* Terms of Service */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <GavelIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                Terms of Service
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              By downloading, installing, or using Daily Stories, you agree to our Terms of Service. These terms govern your use of the app, subscription, content licensing, and other important legal matters.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please review our complete{' '}
              <Link onClick={() => navigate('/terms-of-service')} sx={{ cursor: 'pointer', fontWeight: 500 }}>
                Terms of Service
              </Link>{' '}
              for detailed information about your rights and obligations.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/terms-of-service')}
              sx={{ mt: 2 }}
            >
              View Terms of Service
            </Button>
          </Paper>

          {/* Privacy & Legal */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                Privacy & Legal
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Daily Stories is operated by Plotcamp. We are committed to protecting your privacy and providing a safe, secure experience.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              For detailed information about how we handle your data, please review our{' '}
              <Link onClick={() => navigate('/privacy-policy')} sx={{ cursor: 'pointer', fontWeight: 500 }}>
                Privacy Policy
              </Link>
              .
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/privacy-policy')}
              sx={{ mt: 2 }}
            >
              View Privacy Policy
            </Button>
          </Paper>

          {/* Additional Help */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" paragraph>
              Can't find what you're looking for?
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="mailto:cem@plotcamp.com"
              startIcon={<EmailIcon />}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Contact Support
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', pt: 4, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2024-2025 Plotcamp. All rights reserved.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Daily Stories is an 18+ adult content platform
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Apple, the Apple logo, iPhone, and iPad are trademarks of Apple Inc., registered in the U.S. and other countries.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              App Store is a service mark of Apple Inc.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Support;
